import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, inject, PLATFORM_ID, signal } from '@angular/core';
import { NavigationEnd, RouterOutlet, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';
import { LoadGeneralComponent } from './shared/components-generals/load-general/load-general.component';

const APP_ICONS = [
  'alerta_amarillo',
  'icon_auto',
  'icon_brands',
  'icon_categories',
  'icon_conditions',
  'icon_coverages',
  'icon_deadlines',
  'icon_dealers',
  'icon_personTypes',
  'icon_plans',
  'icon_products',
  'icon_programs',
  'icon_zones',
  'icon_insurances',
  'icon_rates',
  'icon_years',
  'icon_vehicle_uses',
  'icon_models',
] as const;
@Component({
  selector: 'app-root',
  imports: [RouterOutlet, LoadGeneralComponent],
  templateUrl: './app.html',
  styleUrl: './app.css',
})
export class App {
  private readonly document = inject(DOCUMENT);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  protected readonly title = signal(environment.appName || 'Arrendamiento Médico');
  protected readonly themeMode = signal<'light' | 'dark'>('light');
  protected isLoggedIn = false;

  constructor(
    private translate: TranslateService,
    private iconRegistry: MatIconRegistry,
    private router: Router,
    private sanitizer: DomSanitizer,
  ) {
    const lang = this.isBrowser
      ? (localStorage.getItem('lang') ?? environment.lenguage) || 'es'
      : environment.lenguage || 'es';
    this.translate.use(lang);

    if (this.isBrowser) {
      this.initializeTheme();
      this.updateLoginState();
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe(() => {
          this.updateLoginState();
        });
    }

    APP_ICONS.forEach((iconName) => {
      this.iconRegistry.addSvgIcon(
        iconName,
        this.sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/${iconName}.svg`),
      );
    });
  }

  private updateLoginState(): void {
    const token = localStorage.getItem('token');
    this.isLoggedIn =
      !!token && this.router.url !== '/login' && this.router.url !== '/external-access';
  }

  protected toggleTheme(): void {
    const nextTheme = this.themeMode() === 'dark' ? 'light' : 'dark';
    this.applyTheme(nextTheme);
  }

  protected isHomeRoute(): boolean {
    return this.router.url === '/' || this.router.url.startsWith('/home');
  }

  private initializeTheme(): void {
    const savedTheme = localStorage.getItem('theme-mode');

    if (savedTheme === 'dark' || savedTheme === 'light') {
      this.applyTheme(savedTheme);
      return;
    }

    this.applyTheme('light', false);
  }

  private applyTheme(theme: 'light' | 'dark', persist: boolean = true): void {
    this.themeMode.set(theme);

    const body = this.document.body;
    body.classList.remove('light-mode', 'dark-mode');
    body.classList.add(`${theme}-mode`);

    if (persist) {
      localStorage.setItem('theme-mode', theme);
    }
  }
}
